import type { Transaction, TransactionAllocation } from '../types';
import { isCreditWithdrawal } from './withdrawalTypeDisplay';
import { getRecordedFailureReason } from './transactionFailureReason';
import {
  type AllocationFulfillmentSummary,
  formatPartialCompletedStepReason,
  formatPartialSyncStepReason,
  summarizeTransactionAllocations,
} from './transactionAllocationSummary';

export type LifecycleStepState = 'done' | 'current' | 'partial' | 'failed' | 'skipped' | 'upcoming';

export type LifecycleStep = {
  id: string;
  label: string;
  /** Short action description for this stage */
  action: string;
  state: LifecycleStepState;
  /** ISO timestamp when known */
  at?: string | null;
  /** Actor / owner of this stage */
  actor?: string | null;
  /** Failure / skip / context reason */
  reason?: string | null;
};

/** Distinct process templates — game↔wallet (internal) vs outsider↔wallet (gateway). */
export type LifecycleFlowKind =
  | 'withdrawal_game_to_wallet'
  | 'withdrawal_external'
  | 'withdrawal_internal_credit'
  | 'deposit_gateway_to_wallet'
  | 'deposit_gateway_to_game'
  | 'deposit_wallet_to_game'
  | 'deposit_internal';

export type TransactionLifecycleFlow = {
  flowKind: LifecycleFlowKind;
  steps: LifecycleStep[];
  /** Index of the step that failed, or -1 */
  failedStepIndex: number;
  /** Index of the current active step, or -1 when fully done */
  currentStepIndex: number;
  terminalStatus: string;
};

type StepDef = {
  id: string;
  label: string;
  action: string;
  actor: string;
};

const TERMINAL_SUCCESS = new Set(['completed']);
const TERMINAL_FAIL = new Set(['failed', 'rejected', 'cancelled', 'expired']);
const PAYOUT_STATUSES = new Set(['approved', 'processing', 'retry_scheduled', 'in_review_with_provider']);
const CRYPTO_PROVIDERS = new Set(['btcpay', 'zeroxprocessing']);
const FIAT_PROVIDERS = new Set(['linkmepay', 'meld', 'ggusonepay']);
const CASH_PROVIDERS = new Set(['admin_cash', 'shop_player_cash', 'agent_cash', 'super_agent_cash']);

function normalizeStatus(status: string | null | undefined): string {
  return String(status || '').trim().toLowerCase();
}

function providerKey(tx: Transaction): string {
  return String(tx.provider || '').toLowerCase();
}

function syncFlag(tx: Transaction, key: string): boolean {
  const meta = tx.metadata ?? {};
  const sync = tx.syncMetadata ?? {};
  return meta[key] === true || sync[key] === true;
}

function hasGameDepositTarget(tx: Transaction): boolean {
  if (tx.gameProviderId) return true;
  const meta = tx.metadata ?? {};
  if (meta.isMultiGame === true) return true;
  if (Array.isArray(meta.allocations) && meta.allocations.length > 0) return true;
  if (syncFlag(tx, 'isMultiGame')) return true;
  return false;
}

function hasGatewayConfirmSignal(tx: Transaction): boolean {
  if (tx.externalTransactionId) return true;
  if (tx.paymentResult && Object.keys(tx.paymentResult).length > 0) return true;
  const sync = tx.syncMetadata || {};
  if (sync.webhookReceived === true || sync.webhookConfirmed === true) return true;
  if (typeof sync.webhookAt === 'string' && sync.webhookAt) return true;
  if (typeof sync.mainWalletCreditedAt === 'string' && sync.mainWalletCreditedAt) return true;
  if (typeof sync.multiGameProcessedAt === 'string' && sync.multiGameProcessedAt) return true;
  return false;
}

function isGatewayProvider(tx: Transaction): boolean {
  const provider = providerKey(tx);
  return CRYPTO_PROVIDERS.has(provider) || FIAT_PROVIDERS.has(provider);
}

function isGameToWalletWithdrawal(tx: Transaction): boolean {
  if (tx.type !== 'withdrawal') return false;
  if (String(tx.paymentMethod || '').toLowerCase() === 'game_withdrawal') return true;
  if (tx.gameWithdrawalDetail) return true;
  if (String(tx.gameWalletTransaction?.direction || '').toLowerCase() === 'game_to_master') return true;
  return false;
}

function isManualGameDeposit(tx: Transaction): boolean {
  return String(tx.paymentMethod || '').toLowerCase() === 'manual_deposit';
}

function isWalletToGameDeposit(tx: Transaction): boolean {
  if (tx.type !== 'deposit') return false;
  const method = String(tx.paymentMethod || '').toLowerCase();
  const provider = providerKey(tx);
  if (isManualGameDeposit(tx)) return true;
  if (syncFlag(tx, 'isWalletTransfer')) return true;
  if (method === 'wallet_transfer' && provider === 'internal') return true;
  if (provider === 'internal' && tx.gameProviderId && !isGatewayProvider(tx)) return true;
  return false;
}

function isInternalDeposit(tx: Transaction): boolean {
  const provider = providerKey(tx);
  if (provider === 'balance_conversion') return true;
  if (CASH_PROVIDERS.has(provider)) return true;
  if (syncFlag(tx, 'balanceToCredits')) return true;
  return false;
}

/** Classify which process template applies (internal game↔wallet vs gateway outsider↔wallet). */
export function resolveLifecycleFlowKind(tx: Transaction): LifecycleFlowKind {
  if (tx.type === 'withdrawal') {
    if (isGameToWalletWithdrawal(tx)) return 'withdrawal_game_to_wallet';
    if (
      isCreditWithdrawal({
        withdrawalType: tx.paymentMethod,
        provider: tx.provider,
        metadata: tx.metadata ?? null,
      })
    ) {
      return 'withdrawal_internal_credit';
    }
    return 'withdrawal_external';
  }

  if (isInternalDeposit(tx)) return 'deposit_internal';
  if (isWalletToGameDeposit(tx)) return 'deposit_wallet_to_game';
  if (hasGameDepositTarget(tx) && isGatewayProvider(tx)) return 'deposit_gateway_to_game';
  if (isGatewayProvider(tx)) return 'deposit_gateway_to_wallet';
  if (hasGameDepositTarget(tx)) return 'deposit_wallet_to_game';
  return 'deposit_gateway_to_wallet';
}

function needsReviewStep(tx: Transaction, kind: LifecycleFlowKind): boolean {
  const status = normalizeStatus(tx.status);
  const reviewKinds: LifecycleFlowKind[] = [
    'withdrawal_external',
    'withdrawal_internal_credit',
    'deposit_wallet_to_game',
  ];
  if (!reviewKinds.includes(kind)) return false;
  if (kind === 'deposit_wallet_to_game' && !isManualGameDeposit(tx)) return false;

  if (tx.flaggedInfo) return true;
  if (status === 'awaiting_admin') return true;
  if (tx.approvedAt || tx.approvedBy) return true;
  if (tx.type === 'withdrawal' && !isGameToWalletWithdrawal(tx)) return true;
  if (kind === 'deposit_wallet_to_game' && status === 'pending') return true;
  return false;
}

function earliestWalletLegAt(tx: Transaction): string | null {
  const times: string[] = [];
  for (const leg of tx.relatedWalletTransactions || []) {
    if (leg.created_at) times.push(leg.created_at);
  }
  for (const leg of tx.relatedUsdWalletTransactions || []) {
    if (leg.created_at) times.push(leg.created_at);
  }
  if (times.length === 0) return null;
  times.sort();
  return times[0] || null;
}

function anyWalletLegFailed(tx: Transaction): boolean {
  const fail = (s: string) => {
    const n = normalizeStatus(s);
    return n === 'failed' || n === 'rejected' || n === 'cancelled';
  };
  return (
    (tx.relatedWalletTransactions || []).some((l) => fail(l.status)) ||
    (tx.relatedUsdWalletTransactions || []).some((l) => fail(l.status))
  );
}

function stepDefsForKind(kind: LifecycleFlowKind, tx: Transaction): StepDef[] {
  switch (kind) {
    case 'withdrawal_game_to_wallet':
      return [
        { id: 'created', label: 'Pending', action: 'Withdrawal request created', actor: 'System' },
        {
          id: 'transfer',
          label: 'Game → wallet',
          action: 'Move credits from game wallet to master wallet',
          actor: 'Game provider',
        },
        { id: 'completed', label: 'Completed', action: 'Credits available in master wallet', actor: 'System' },
      ];
    case 'withdrawal_external':
      return [
        { id: 'created', label: 'Pending', action: 'Withdrawal request created', actor: 'System' },
        {
          id: 'review',
          label: 'Await approval',
          action: tx.flaggedInfo ? 'Admin / shop review' : 'Admin or shop approval',
          actor: 'Admin / Shop',
        },
        {
          id: 'payout',
          label: 'Send payout',
          action: 'Submit payout to gateway / provider',
          actor: 'Gateway',
        },
        {
          id: 'confirm',
          label: 'Confirm',
          action: 'Provider / webhook confirmation',
          actor: 'Gateway',
        },
        { id: 'completed', label: 'Completed', action: 'Funds sent to recipient', actor: 'System' },
      ];
    case 'withdrawal_internal_credit':
      return [
        { id: 'created', label: 'Pending', action: 'Credit withdrawal request created', actor: 'System' },
        {
          id: 'review',
          label: 'Await approval',
          action: 'Internal approval when required',
          actor: 'Admin / Shop',
        },
        {
          id: 'transfer',
          label: 'Transfer credits',
          action: 'Move credits between internal wallets',
          actor: 'Internal',
        },
        { id: 'completed', label: 'Completed', action: 'Credit transfer finalized', actor: 'System' },
      ];
    case 'deposit_gateway_to_wallet':
      return [
        { id: 'created', label: 'Pending', action: 'Payment initiated', actor: 'System' },
        { id: 'payment', label: 'Gateway payment', action: 'Await / process external payment', actor: 'Gateway' },
        { id: 'webhook', label: 'Confirm payment', action: 'Gateway confirmation received', actor: 'Gateway' },
        { id: 'credit', label: 'Credit wallet', action: 'Apply credits / USD to master wallet', actor: 'System' },
        { id: 'completed', label: 'Completed', action: 'Wallet credited', actor: 'System' },
      ];
    case 'deposit_gateway_to_game':
      return [
        { id: 'created', label: 'Pending', action: 'Deposit initiated', actor: 'System' },
        { id: 'payment', label: 'Gateway payment', action: 'Charge external payment method', actor: 'Gateway' },
        { id: 'webhook', label: 'Confirm payment', action: 'Gateway confirmation received', actor: 'Gateway' },
        {
          id: 'fund',
          label: 'Fund & allocate',
          action: 'Credit master wallet and allocate to game(s)',
          actor: 'System',
        },
        {
          id: 'sync',
          label: 'Game sync',
          action: 'Sync credits to game provider wallet(s)',
          actor: 'Game provider',
        },
        { id: 'completed', label: 'Completed', action: 'Credits playable in game', actor: 'System' },
      ];
    case 'deposit_wallet_to_game': {
      const steps: StepDef[] = [
        { id: 'created', label: 'Pending', action: 'Transfer request created', actor: 'System' },
      ];
      if (isManualGameDeposit(tx)) {
        steps.push({
          id: 'review',
          label: 'Admin review',
          action: 'Admin confirms manual game deposit',
          actor: 'Admin',
        });
      }
      steps.push(
        {
          id: 'debit',
          label: 'Debit master',
          action: 'Debit / reserve credits from master wallet',
          actor: 'System',
        },
        {
          id: 'sync',
          label: 'Game sync',
          action: 'Transfer to game wallet / provider sync',
          actor: 'Game provider',
        },
        { id: 'completed', label: 'Completed', action: 'Credits available in game', actor: 'System' },
      );
      return steps;
    }
    case 'deposit_internal':
      return [
        { id: 'created', label: 'Pending', action: 'Internal deposit initiated', actor: 'System' },
        {
          id: 'fulfill',
          label: 'Fulfill',
          action: 'Apply internal credit / cash settlement',
          actor: 'Admin / System',
        },
        { id: 'completed', label: 'Completed', action: 'Deposit finalized', actor: 'System' },
      ];
    default:
      return [];
  }
}

function resolveActiveStepId(tx: Transaction, kind: LifecycleFlowKind, defs: StepDef[]): string {
  const status = normalizeStatus(tx.status);
  const ids = new Set(defs.map((d) => d.id));

  if (TERMINAL_SUCCESS.has(status)) return 'completed';

  if (status === 'rejected') {
    if (ids.has('review')) return 'review';
    return 'created';
  }
  if (status === 'cancelled' || status === 'expired') {
    if (ids.has('review') && (tx.approvedAt || status === 'expired')) return 'review';
    return 'created';
  }
  if (status === 'failed') {
    if (anyWalletLegFailed(tx)) {
      if (ids.has('sync')) return 'sync';
      if (ids.has('transfer')) return 'transfer';
      if (ids.has('credit')) return 'credit';
      if (ids.has('confirm')) return 'confirm';
    }
    if (hasGatewayConfirmSignal(tx)) {
      if (ids.has('fund')) return 'fund';
      if (ids.has('webhook')) return 'webhook';
      if (ids.has('confirm')) return 'confirm';
    }
    if (ids.has('payout')) return 'payout';
    if (ids.has('payment')) return 'payment';
    if (ids.has('debit')) return 'debit';
    if (ids.has('fulfill')) return 'fulfill';
    return defs[Math.max(0, defs.length - 2)]?.id || 'created';
  }

  if (status === 'awaiting_admin') return ids.has('review') ? 'review' : 'created';

  if (status === 'pending') {
    if (kind === 'deposit_wallet_to_game') {
      if (ids.has('review') && isManualGameDeposit(tx) && (tx.flaggedInfo || !tx.approvedAt)) {
        return 'review';
      }
      return earliestWalletLegAt(tx) ? 'sync' : 'debit';
    }
    if (kind === 'withdrawal_game_to_wallet') {
      return tx.gameWalletTransaction || earliestWalletLegAt(tx) ? 'transfer' : 'created';
    }
    if (kind === 'deposit_gateway_to_game') {
      if (hasGatewayConfirmSignal(tx)) return ids.has('fund') ? 'fund' : 'webhook';
      return ids.has('payment') ? 'payment' : 'created';
    }
    if (ids.has('review') && (tx.flaggedInfo || kind === 'withdrawal_external')) return 'review';
    if (ids.has('payment')) return 'payment';
    return 'created';
  }

  if (PAYOUT_STATUSES.has(status)) {
    if (ids.has('payout')) return 'payout';
    if (ids.has('payment')) return 'payment';
  }

  if (ids.has('sync') && (tx.gameWalletTransaction || earliestWalletLegAt(tx))) return 'sync';
  if (ids.has('fund') && hasGatewayConfirmSignal(tx)) return 'fund';
  if (ids.has('credit') && earliestWalletLegAt(tx)) return 'credit';

  return defs[0]?.id || 'created';
}

function timestampForStep(tx: Transaction, stepId: string): string | null {
  const sync = tx.syncMetadata || {};

  switch (stepId) {
    case 'created':
      return tx.created_at || null;
    case 'review':
      return tx.approvedAt || (normalizeStatus(tx.status) === 'awaiting_admin' ? tx.updated_at : null);
    case 'payout':
    case 'payment':
      return earliestWalletLegAt(tx) || (PAYOUT_STATUSES.has(normalizeStatus(tx.status)) ? tx.updated_at : null);
    case 'debit':
    case 'fund':
    case 'fulfill':
      return earliestWalletLegAt(tx) || tx.updated_at || null;
    case 'transfer':
    case 'sync':
      return (
        tx.gameWithdrawalDetail?.createdAt ||
        tx.gameWalletTransaction?.createdAt ||
        earliestWalletLegAt(tx) ||
        null
      );
    case 'webhook':
    case 'confirm': {
      if (typeof sync.webhookAt === 'string') return sync.webhookAt;
      if (typeof sync.mainWalletCreditedAt === 'string') return sync.mainWalletCreditedAt;
      return hasGatewayConfirmSignal(tx) ? tx.updated_at : null;
    }
    case 'credit':
      return earliestWalletLegAt(tx) || null;
    case 'completed':
      return TERMINAL_SUCCESS.has(normalizeStatus(tx.status)) ? tx.updated_at : null;
    default:
      return null;
  }
}

function actorForStep(tx: Transaction, def: StepDef): string {
  if (def.id === 'review') {
    if (tx.approvedBy) return String(tx.approvedBy);
    if (tx.flaggedInfo?.opsReviewOnly) return 'Admin';
    return def.actor;
  }
  return def.actor;
}

const FLOW_KIND_LABELS: Record<LifecycleFlowKind, string> = {
  withdrawal_game_to_wallet: 'Game → master wallet',
  withdrawal_external: 'Wallet → external payout',
  withdrawal_internal_credit: 'Internal credit withdrawal',
  deposit_gateway_to_wallet: 'External payment → wallet',
  deposit_gateway_to_game: 'External payment → game',
  deposit_wallet_to_game: 'Master wallet → game',
  deposit_internal: 'Internal deposit',
};

/** Human-readable flow category for the process flow header. */
export function getLifecycleFlowKindLabel(kind: LifecycleFlowKind): string {
  return FLOW_KIND_LABELS[kind] ?? kind;
}

export type BuildTransactionLifecycleFlowOptions = {
  /** From GET /admin/transactions/:id/allocations — required for accurate multi-game sync status. */
  allocations?: TransactionAllocation[];
};

function applyAllocationFulfillmentOverlay(
  steps: LifecycleStep[],
  flowKind: LifecycleFlowKind,
  summary: AllocationFulfillmentSummary,
): LifecycleStep[] {
  if (flowKind !== 'deposit_wallet_to_game' && flowKind !== 'deposit_gateway_to_game') {
    return steps;
  }
  const syncIdx = steps.findIndex((s) => s.id === 'sync');
  const completedIdx = steps.findIndex((s) => s.id === 'completed');
  if (syncIdx < 0 || summary.total === 0) return steps;

  const next = steps.map((s) => ({
    ...s,
    reason: s.id === 'sync' || s.id === 'completed' ? null : s.reason,
  }));

  for (let i = 0; i < syncIdx; i++) {
    if (next[i].state !== 'skipped') next[i].state = 'done';
  }

  const fs = summary.fulfillmentStatus;
  if (fs === 'completed') {
    next[syncIdx].state = 'done';
    if (completedIdx >= 0) next[completedIdx].state = 'done';
    return next;
  }
  if (fs === 'pending') {
    next[syncIdx].state = 'current';
    if (completedIdx >= 0) next[completedIdx].state = 'upcoming';
    return next;
  }
  if (fs === 'refunded' || fs === 'failed' || fs === 'rejected') {
    next[syncIdx].state = 'failed';
    next[syncIdx].reason = summary.syncStepReason || `Game sync ${fs}`;
    if (completedIdx >= 0) next[completedIdx].state = 'upcoming';
    return next;
  }
  if (fs === 'partial') {
    if (summary.pending > 0) {
      next[syncIdx].state = 'current';
      next[syncIdx].reason = summary.syncStepReason;
      if (completedIdx >= 0) next[completedIdx].state = 'upcoming';
    } else {
      next[syncIdx].state = 'partial';
      next[syncIdx].reason = formatPartialSyncStepReason(summary);
      if (completedIdx >= 0) {
        next[completedIdx].state = 'partial';
        next[completedIdx].reason = formatPartialCompletedStepReason();
      }
    }
    return next;
  }
  return next;
}

/**
 * Build a derived lifecycle flowchart from GET /admin/transactions/:id payload.
 * Flow kind is inferred from provider, paymentMethod, syncMetadata, and game wallet signals.
 */
export function buildTransactionLifecycleFlow(
  tx: Transaction,
  options?: BuildTransactionLifecycleFlowOptions,
): TransactionLifecycleFlow {
  const status = normalizeStatus(tx.status);
  const flowKind = resolveLifecycleFlowKind(tx);
  const defs = stepDefsForKind(flowKind, tx);
  const activeId = resolveActiveStepId(tx, flowKind, defs);
  const activeIndex = Math.max(0, defs.findIndex((d) => d.id === activeId));
  const isFail = TERMINAL_FAIL.has(status);
  const isDone = TERMINAL_SUCCESS.has(status);
  const failureReason = getRecordedFailureReason({
    errorMessage: tx.errorMessage,
    syncMetadata: tx.syncMetadata,
  });

  const reviewRequired = needsReviewStep(tx, flowKind);

  const steps: LifecycleStep[] = defs.map((def, index) => {
    let state: LifecycleStepState = 'upcoming';
    let reason: string | null = null;

    if (def.id === 'review' && !reviewRequired && activeId !== 'review') {
      return {
        id: def.id,
        label: def.label,
        action: def.action,
        state: 'skipped',
        at: null,
        actor: def.actor,
        reason: 'Not required / auto-processed',
      };
    }

    if (isDone) {
      state = 'done';
    } else if (isFail) {
      if (index < activeIndex) state = 'done';
      else if (index === activeIndex) {
        state = 'failed';
        reason = failureReason || `Stopped with status: ${status}`;
      } else {
        state = 'upcoming';
      }
    } else {
      if (index < activeIndex) state = 'done';
      else if (index === activeIndex) state = 'current';
      else state = 'upcoming';
    }

    return {
      id: def.id,
      label: def.label,
      action: def.action,
      state,
      at: timestampForStep(tx, def.id),
      actor: actorForStep(tx, def),
      reason,
    };
  });

  let finalSteps = steps;
  if (options?.allocations?.length) {
    const summary = summarizeTransactionAllocations(options.allocations, status);
    finalSteps = applyAllocationFulfillmentOverlay(finalSteps, flowKind, summary);
  }

  const failedStepIndex = finalSteps.findIndex((s) => s.state === 'failed');
  const hasPartialTerminal = finalSteps.some((s) => s.state === 'partial');
  const currentStepIndex = finalSteps.some((s) => s.state === 'current')
    ? finalSteps.findIndex((s) => s.state === 'current')
    : hasPartialTerminal
      ? -1
      : isDone
        ? finalSteps.length - 1
        : -1;

  return {
    flowKind,
    steps: finalSteps,
    failedStepIndex,
    currentStepIndex,
    terminalStatus: status,
  };
}
